import React, { useEffect, useState, useCallback } from 'react';
import { responseHero } from '../../../utils/axiosInterceptor';
import { formatCurrency } from '../utils/formatCurrency';
import { BMP_CryptoTrades, GetWalletResponse } from '../../../models/bmp.model';
import { getBMPWalletState } from '../../../api/getBMPWalletState';
import Decimal from 'decimal.js';
import { AddNewBuyModal } from './modals/AddNewBuyModal';
import { formatCrypto } from '../../../utils/formatCrypto';

interface TradingWalletProps {
    value: number;
    onBack: () => void;
    onUpdate: () => void; // A globális walletState frissítéséhez
}

export const TradingWalletComponent: React.FC<TradingWalletProps> = ({ value, onBack, onUpdate }) => {
    const [trades, setTrades] = useState<BMP_CryptoTrades[]>([]);
    const [walletData, setWalletData] = useState<GetWalletResponse | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [tradesRes, walletRes] = await Promise.all([
                responseHero.get(`${process.env.REACT_APP_API_URL}/bmp/trading/crypto`),
                getBMPWalletState()
            ]);
            setTrades(tradesRes.data);
            setWalletData(walletRes);
        } catch (err) {
            console.error("Hiba az adatok lekérésekor:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDelete = async (id: string) => {
        if (!window.confirm("Biztosan törölni szeretnéd ezt a tranzakciót?")) return;
        try {
            await responseHero.delete(`${process.env.REACT_APP_API_URL}/bmp/trading/crypto/${id}`);
            await fetchData(); // Helyi lista frissítése
            onUpdate();       // Szülő (likviditás) frissítése
        } catch (err) {
            alert("Hiba történt a törlés során.");
        }
    };

    const getAssetUsd = (val: any) => val ? new Decimal(val).toNumber() : 0;
    return (
        <div className="w-full max-w-6xl mx-auto p-6 animate-in fade-in duration-700">
            <div className="flex justify-between items-center mb-8">
                <button onClick={onBack} className="text-slate-500 hover:text-slate-800 font-bold flex items-center gap-2 transition-colors group">
                    <span className="group-hover:-translate-x-1 transition-transform">←</span> Vissza a portfólióhoz
                </button>

                <button 
                    onClick={() => setIsModalOpen(true)}
                    className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-3 rounded-2xl font-black uppercase text-xs tracking-widest shadow-lg shadow-amber-600/20 transition-all active:scale-95"
                >
                    Vásárlás
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
                <div className="lg:col-span-2 bg-slate-900 rounded-[2.5rem] p-10 text-white shadow-2xl relative overflow-hidden flex flex-col justify-center min-h-[220px]">
                    <div className="relative z-10">
                        <h2 className="text-amber-500 text-[10px] font-black uppercase tracking-[0.3em] mb-3">Trading Portfolio Összérték</h2>
                        <div className="text-6xl font-black tracking-tighter">
                            {formatCurrency(value)}
                        </div>
                        <p className="mt-4 text-slate-400 text-xs font-medium max-w-md">
                            Az aktív kriptovaluta pozíciók és a szabad trading likviditás együttes értéke.
                        </p>
                    </div>
                    <div className="absolute right-[-20px] bottom-[-40px] opacity-[0.03] text-[15rem] font-black italic select-none">TRD</div>
                </div>

                <div className="bg-white border-2 border-slate-100 rounded-[2.5rem] p-8 flex flex-col justify-center shadow-sm">
                    <span className="text-slate-400 text-[10px] font-black uppercase tracking-widest mb-2">Szabad Likviditás (USD)</span>
                    <div className="text-3xl font-black text-slate-800">
                        {formatCurrency(getAssetUsd(walletData?.wallet.tradingLiquidity))}
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-emerald-500 font-bold text-xs">
                        <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                        Kereskedésre kész
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
                <div className="bg-amber-50/50 border border-amber-100 rounded-3xl p-8 flex justify-between items-center group hover:bg-amber-50 transition-all hover:shadow-md">
                    <div>
                        <p className="text-amber-600 text-[10px] font-black uppercase tracking-widest mb-1">Bitcoin Pozíció</p>
                        <div className="text-3xl font-black text-slate-900 mb-1">
                            {formatCurrency(getAssetUsd(walletData?.holdingsUsd?.tradingBtc))}
                        </div>
                        <p className="text-sm font-bold text-slate-500">
                            {formatCrypto(walletData?.wallet.tradingBtc)} <span className="text-[10px] opacity-60">BTC</span>
                        </p>
                    </div>
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm text-2xl group-hover:scale-110 transition-transform">₿</div>
                </div>

                <div className="bg-indigo-50/50 border border-indigo-100 rounded-3xl p-8 flex justify-between items-center group hover:bg-indigo-50 transition-all hover:shadow-md">
                    <div>
                        <p className="text-indigo-600 text-[10px] font-black uppercase tracking-widest mb-1">Ethereum Pozíció</p>
                        <div className="text-3xl font-black text-slate-900 mb-1">
                            {formatCurrency(getAssetUsd(walletData?.holdingsUsd?.tradingEth))}
                        </div>
                        <p className="text-sm font-bold text-slate-500">
                            {formatCrypto(walletData?.wallet.tradingEth)} <span className="text-[10px] opacity-60">ETH</span>
                        </p>
                    </div>
                    <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center shadow-sm text-2xl group-hover:scale-110 transition-transform">Ξ</div>
                </div>
            </div>

            <div className="bg-white border border-slate-100 rounded-[2.5rem] shadow-sm overflow-hidden mb-10">
                <div className="px-10 py-8 border-b border-slate-50 flex justify-between items-center">
                    <h3 className="font-black text-slate-800 uppercase tracking-widest text-xs">Tranzakciós Előzmények</h3>
                    <div className="px-3 py-1 bg-slate-100 rounded-full text-[9px] font-bold text-slate-500 uppercase">{trades.length} tétel</div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-slate-50/50 text-[10px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-50">
                                <th className="px-10 py-5">Dátum</th>
                                <th className="px-6 py-5">Típus</th>
                                <th className="px-6 py-5">Eszköz</th>
                                <th className="px-6 py-5 text-right">Mennyiség</th>
                                <th className="px-6 py-5 text-right">Árfolyam</th>
                                <th className="px-6 py-5 text-right">Összeg (USD)</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {trades.map((trade) => (
                                <tr key={trade.id} className="hover:bg-slate-50/30 transition-colors group">
                                    <td className="px-10 py-5 text-sm font-medium text-slate-500">
                                        {new Date(trade.date).toLocaleDateString('hu-HU')}
                                    </td>
                                    <td className="px-6 py-5">
                                        <span className={`px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-tighter ${
                                            trade.operation 
                                            ? 'bg-emerald-100 text-emerald-700' 
                                            : 'bg-rose-100 text-rose-700'
                                        }`}>
                                            {trade.operation ? 'Vétel' : 'Eladás'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-5 font-bold text-slate-700">
                                        {trade.assetType === 1 ? 'BTC' : trade.assetType === 2 ? 'ETH' : 'Egyéb'}
                                    </td>
                                    <td className="px-6 py-5 text-right font-mono text-sm font-bold text-slate-600">
                                        {new Decimal(trade.quantity || 0).toString()}
                                    </td>
                                    <td className="px-6 py-5 text-right text-slate-400 text-xs">
                                        {formatCurrency(new Decimal(trade.exchange_rate || 0).toNumber())}
                                    </td>
                                    <td className="px-6 py-5 text-right font-black text-slate-900">
                                        {formatCurrency(new Decimal(trade.amount || 0).toNumber())}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <AddNewBuyModal 
                isOpen={isModalOpen} 
                onClose={() => setIsModalOpen(false)} 
                onSuccess={() => { setIsModalOpen(false); fetchData(); onUpdate(); }}
            />
        </div>
    );
};